export type FieldType =
  | 'id'
  | 'enum'
  | 'string'
  | 'text'
  | 'integer'
  | 'decimal'
  | 'date'
  | 'datetime'
  | 'boolean'
  | 'count'

export type Operator =
  | 'equals'
  | 'not_equals'
  | 'in'
  | 'not_in'
  | 'gt'
  | 'gte'
  | 'lt'
  | 'lte'
  | 'between'
  | 'contains'
  | 'starts_with'
  | 'is_null'

export type ValueSource = 'records' | 'values'

export interface FieldValue {
  value: string
  label: string
}

export interface RegistryField {
  key: string
  label: string
  type: FieldType
  operators: Operator[]
  values: FieldValue[] | null
  options: { source: ValueSource } | null
  filterable: boolean
}

export interface RegistryRelation {
  label: string
  fields: RegistryField[]
}

export interface RegistryEntity {
  key: string
  label: string
  fields: RegistryField[]
  default_columns: string[]
  relations: Record<string, RegistryRelation>
}

export interface BaseEntityOption {
  key: string
  label: string
}

export interface RuleCondition {
  id: string
  type: 'condition'
  field: string | null
  operator: Operator | null
  value: unknown
}

export interface RuleGroup {
  id: string
  type: 'group'
  combinator: 'and' | 'or'
  conditions: (RuleCondition | RuleGroup)[]
}

export type RuleNode = RuleCondition | RuleGroup

export interface ReportSort {
  field: string
  direction: 'asc' | 'desc'
}

export interface ReportCountDateRange {
  from: string
  to: string
}

export interface SavedReport {
  id: number
  slug: string
  name: string
  base_entity: string
  created_at: string
}

export interface SavedReportDetail extends SavedReport {
  rule_tree: RuleGroup | null
  columns: string[]
  sort: ReportSort | null
  count_date_range: ReportCountDateRange | null
}

export type ExportStatus = 'pending' | 'processing' | 'completed' | 'failed'

export interface RecentExport {
  id: number
  base_entity: string
  status: ExportStatus
  error_message: string | null
  filename: string | null
  created_at: string
  completed_at: string | null
  expires_at: string | null
}

let nextId = 0
export function ruleNodeId(): string {
  nextId += 1
  return `rule-${Date.now()}-${nextId}`
}

export function emptyGroup(combinator: 'and' | 'or' = 'and'): RuleGroup {
  return { id: ruleNodeId(), type: 'group', combinator, conditions: [] }
}

export function emptyCondition(): RuleCondition {
  return { id: ruleNodeId(), type: 'condition', field: null, operator: null, value: null }
}

export function serializeRuleTree(node: RuleNode | null): unknown {
  if (!node) return null
  if (node.type === 'condition') {
    return { type: 'condition', field: node.field, operator: node.operator, value: node.value }
  }
  return {
    type: 'group',
    combinator: node.combinator,
    conditions: node.conditions.map((c) => serializeRuleTree(c)),
  }
}

function conditionIsComplete(c: RuleCondition): boolean {
  if (!c.field || !c.operator) return false
  if (c.operator === 'is_null') return true
  if (c.operator === 'between') return Array.isArray(c.value) && c.value[0] != null && c.value[1] != null
  if (c.operator === 'in' || c.operator === 'not_in') return Array.isArray(c.value) && c.value.length > 0
  return c.value !== null && c.value !== undefined && c.value !== ''
}

export function pruneRuleTree(node: RuleNode | null): RuleNode | null {
  if (!node) return null
  if (node.type === 'condition') return conditionIsComplete(node) ? node : null

  const pruned = node.conditions.map((c) => pruneRuleTree(c)).filter((c): c is RuleNode => c !== null)
  return pruned.length > 0 ? { ...node, conditions: pruned } : null
}

export function allFields(entity: RegistryEntity): { path: string; label: string; field: RegistryField }[] {
  const own = entity.fields.map((f) => ({ path: f.key, label: f.label, field: f }))
  const related = Object.entries(entity.relations).flatMap(([relKey, rel]) =>
    rel.fields.map((f) => ({
      path: `${relKey}.${f.key}`,
      label: f.label === rel.label ? rel.label : `${rel.label}: ${f.label}`,
      field: f,
    })),
  )
  return [...own, ...related]
}

export function hasCountFields(entity: RegistryEntity): boolean {
  return entity.fields.some((f) => f.type === 'count')
}
